import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  ArrowLeft,
  Camera,
  ChevronDown,
  ChevronUp,
  Fuel,
  LogOut,
  Plus,
  X,
} from 'lucide-react'
import { Card, CardContent } from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import Alert from '../../components/ui/Alert'
import Loading from '../../components/ui/Loading'
import { useAuth } from '../../contexts/AuthContext'
import { useFuelDiscounts } from '../../hooks/useFuelDiscounts'
import { formatDate } from '../../utils'
import type { FuelDiscount, FuelDiscountGroup } from '../../types'
import { formatGallons } from '../../utils/formatQuantity'

function getGroupTitle(group: FuelDiscountGroup): string {
  const cc = group.costCenterId ? `CC ${group.costCenterId}` : ''
  const machine = [group.machineBrand, group.machineModel].filter(Boolean).join(' · ')
  return [cc, machine, group.machineCategoryName, group.companyName]
    .filter(Boolean)
    .join(' - ') || 'Sin nombre'
}

const FuelLoads: React.FC = () => {
  const navigate = useNavigate()
  const { operator, logout } = useAuth()
  const { fuelGroups, stats, loading, error, refetch } = useFuelDiscounts()
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())
  const [selected, setSelected] = useState<FuelDiscount | null>(null)

  const isOperator = operator?.role !== 'resident' && operator?.role !== 'supervisor'

  const toggle = (key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  const sections = useMemo(
    () =>
      fuelGroups.map((g) => ({
        key: String(g.costCenterId),
        title: getGroupTitle(g),
        totalLoads: g.totalLoads,
        totalGallons: g.totalGallons,
        discounts: g.fuelDiscounts,
      })),
    [fuelGroups],
  )

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  return (
    <div className="min-h-screen bg-gray-200">
      <header className="bg-retri-blue shadow-md">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center py-3 sm:py-4">
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/dashboard')}
                title="Volver al dashboard"
                className="text-white hover:bg-retri-bluedark"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <Fuel className="h-5 w-5 text-white" />
              <div className="min-w-0">
                <h1 className="text-sm sm:text-xl font-bold text-white truncate">
                  Cargues de Combustible
                </h1>
                <p className="text-xs sm:text-sm text-blue-200 truncate">
                  Historial de cargues registrados
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleLogout}
              title="Cerrar sesión"
              className="flex-shrink-0 text-white hover:bg-retri-bluedark"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8 py-4 sm:py-8">
        <div className="space-y-4 sm:space-y-6">
          {error ? (
            <Alert variant="error" title="Error">
              {error}
              <Button variant="outline" size="sm" onClick={refetch} className="mt-2">
                Reintentar
              </Button>
            </Alert>
          ) : null}

          <div className="grid grid-cols-2 gap-3">
            <Card>
              <CardContent className="p-4 flex flex-col items-center gap-1">
                <Fuel className="h-6 w-6 text-retri-blue" />
                <p className="text-xs text-gray-500 leading-none">Cargues totales</p>
                <p className="text-2xl font-bold text-gray-900 leading-none">{stats.totalLoads}</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="p-4 flex flex-col items-center gap-1">
                <p className="text-xs text-gray-500 leading-none">Galones totales</p>
                <p className="text-2xl font-bold text-gray-900 leading-none">
                  {formatGallons(stats.totalGallons)}
                </p>
                <p className="text-xs text-gray-400 leading-none">gal</p>
              </CardContent>
            </Card>
          </div>

          {isOperator ? (
            <Button
              variant="primary"
              size="default"
              onClick={() => navigate('/create-fuel-load')}
              className="w-full justify-center h-12"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuevo cargue
            </Button>
          ) : null}

          {loading ? (
            <div className="py-12">
              <Loading size="lg" text="Cargando cargues..." />
            </div>
          ) : sections.length === 0 ? (
            <div className="text-center py-8 sm:py-12">
              <Fuel className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
              <h3 className="mt-4 text-base sm:text-lg font-medium text-gray-900">
                Sin cargues registrados
              </h3>
              <p className="mt-2 text-sm text-gray-500">
                Cuando registres un cargue de combustible aparecerá aquí.
              </p>
            </div>
          ) : (
            <div className="space-y-6">
              {sections.map((section) => {
                const isCollapsed = collapsed.has(section.key)
                return (
                  <div key={section.key} className="space-y-3">
                    <button
                      onClick={() => toggle(section.key)}
                      className="w-full sticky top-0 bg-retri-blue px-4 py-2 rounded-t-lg flex items-center justify-between"
                    >
                      <div className="text-left min-w-0 pr-2">
                        <h3 className="font-semibold text-white text-sm truncate">
                          {section.title}
                        </h3>
                        <p className="text-xs text-blue-100">
                          {section.totalLoads} {section.totalLoads === 1 ? 'cargue' : 'cargues'} ·{' '}
                          {formatGallons(section.totalGallons)} gal
                        </p>
                      </div>
                      {isCollapsed ? (
                        <ChevronDown className="h-4 w-4 text-white flex-shrink-0" />
                      ) : (
                        <ChevronUp className="h-4 w-4 text-white flex-shrink-0" />
                      )}
                    </button>

                    {!isCollapsed
                      ? section.discounts.map((d) => (
                          <FuelCard key={d.id} discount={d} onClick={() => setSelected(d)} />
                        ))
                      : null}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </main>

      {selected ? (
        <FuelDetailModal discount={selected} onClose={() => setSelected(null)} />
      ) : null}
    </div>
  )
}

function FuelCard({ discount, onClick }: { discount: FuelDiscount; onClick: () => void }) {
  const machine = [discount.machinery?.brand, discount.machinery?.model].filter(Boolean).join(' · ')
  return (
    <button
      onClick={onClick}
      className="w-full text-left border border-gray-200 rounded-lg p-4 bg-white hover:shadow-md transition-shadow"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold text-gray-900">{formatDate(discount.date)}</span>
        <span className="text-base font-bold text-retri-blue">
          {formatGallons(discount.gallons)} gal
        </span>
      </div>
      {machine ? <p className="text-xs text-gray-500 truncate">{machine}</p> : null}
      {discount.operator?.name ? (
        <p className="text-xs text-gray-500 truncate">Operario: {discount.operator.name}</p>
      ) : null}
      {discount.observations ? (
        <p className="text-xs text-gray-600 mt-2 line-clamp-2">{discount.observations}</p>
      ) : null}
      {discount.photo ? (
        <div className="mt-2 flex items-center gap-1 text-xs text-retri-blue">
          <Camera className="h-3 w-3" />
          <span>Ver comprobante</span>
        </div>
      ) : null}
    </button>
  )
}

function FuelDetailModal({
  discount,
  onClose,
}: {
  discount: FuelDiscount
  onClose: () => void
}) {
  const machine = [discount.machinery?.brand, discount.machinery?.model].filter(Boolean).join(' · ')
  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 p-4">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Cargue de combustible</h2>
            <p className="text-sm text-gray-500">{formatDate(discount.date)}</p>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} aria-label="Cerrar">
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-4 space-y-3">
          <Row label="Galones" value={`${formatGallons(discount.gallons)} gal`} />
          {discount.operator?.name ? (
            <Row label="Operario" value={discount.operator.name} />
          ) : null}
          {machine ? <Row label="Máquina" value={machine} /> : null}
          {discount.machinery?.serial ? (
            <Row label="Serial" value={discount.machinery.serial} />
          ) : null}
          {discount.costCenterId ? (
            <Row label="Centro de costo" value={`CC ${discount.costCenterId}`} />
          ) : null}
          {discount.company?.name ? (
            <Row label="Negocio" value={discount.company.name} />
          ) : null}
          {discount.observations ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Observaciones</p>
              <p className="text-sm text-gray-800">{discount.observations}</p>
            </div>
          ) : null}
          {discount.photo ? (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase mb-2">
                Foto del comprobante
              </p>
              <img
                src={discount.photo}
                alt="Comprobante"
                className="w-full rounded-md border border-gray-200"
              />
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-gray-100 py-2">
      <span className="text-xs font-semibold text-gray-500 uppercase">{label}</span>
      <span className="text-sm font-medium text-gray-900 text-right">{value}</span>
    </div>
  )
}

export default FuelLoads
